import { StreamingCallbacks } from './types';
import { ConnectionManager } from './connection-manager';

export class SSEParser {
  private static readonly STREAM_TIMEOUT = 180000; // 180s for very long streams

  static async parseSSEStream(
    body: ReadableStream<Uint8Array>,
    callbacks: StreamingCallbacks,
    requestId: string,
    abortController: AbortController
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    let eventCount = 0;
    let hasReceivedContent = false;

    console.log('[SSE PARSER STREAM FIX] 🔄 Starting SSE stream parsing (no blocking):', {
      requestId,
      streamTimeout: this.STREAM_TIMEOUT
    });

    const streamTimeoutId = setTimeout(() => {
      console.warn('[SSE PARSER STREAM FIX] ⏰ Stream timeout, aborting:', {
        requestId,
        timeout: this.STREAM_TIMEOUT,
        eventCount,
        hasReceivedContent
      });
      abortController.abort();
    }, this.STREAM_TIMEOUT);

    try {
      while (!abortController.signal.aborted) {
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('[SSE PARSER STREAM FIX] ✅ SSE stream completed:', { 
            requestId,
            fullContentLength: fullContent.length,
            eventCount,
            hasReceivedContent
          });
          break;
        }

        if (!value) continue;

        eventCount++;
        ConnectionManager.updateConnectionStatus(requestId, 'streaming');

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue;
          
          console.log('[SSE PARSER STREAM FIX] 📝 Processing SSE line:', {
            requestId,
            linePreview: line.substring(0, 100),
            eventCount
          });
          
          if (line.startsWith('data: ')) {
            const dataContent = line.slice(6);
            if (dataContent === '[DONE]') {
              console.log('[SSE PARSER STREAM FIX] 🏁 Stream completed with [DONE]:', { 
                requestId,
                finalContentLength: fullContent.length,
                eventCount,
                hasReceivedContent
              });
              
              // STEP 1: Always call onComplete when stream ends
              if (callbacks.onComplete) {
                callbacks.onComplete({
                  requestId,
                  messageId: crypto.randomUUID(),
                  content: fullContent,
                  suggestions: [],
                  model: 'gpt-4',
                  usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
                  knowledgeUsed: false,
                  knowledgeResults: [],
                  suggestionsSource: 'api',
                  contextTruncated: false,
                  queryExpansionUsed: false
                });
              }
              
              clearTimeout(streamTimeoutId);
              return;
            }

            try {
              const parsed = this.parseSSEDataSafely(dataContent, requestId);
              if (parsed) {
                parsed.requestId = parsed.requestId || requestId;
                console.log('[SSE PARSER STREAM FIX] 🎯 Parsed SSE event (no blocking):', {
                  requestId,
                  eventType: parsed.type,
                  hasContent: !!parsed.content || !!parsed.chunk,
                  eventSequence: parsed.sequence
                });
                
                // Track if we've received any content
                if (parsed.type === 'content' && (parsed.chunk || parsed.fullContent)) {
                  hasReceivedContent = true;
                }
                
                // STEP 1: Process all events without blocking
                this.handleSSEEventWithoutBlocking(parsed, callbacks, requestId, fullContent);
                
                if (parsed.type === 'content' && parsed.fullContent) {
                  fullContent = parsed.fullContent;
                }
              }
              
            } catch (parseError) {
              console.warn('[SSE PARSER STREAM FIX] ⚠️ Parse error (treating as raw content):', {
                error: parseError instanceof Error ? parseError.message : String(parseError),
                data: dataContent.substring(0, 100) + '...',
                requestId
              });
              
              // STEP 1: Process raw content without blocking
              if (dataContent && callbacks.onContent) {
                hasReceivedContent = true;
                fullContent += dataContent;
                callbacks.onContent(dataContent, fullContent, requestId);
                console.log('[SSE PARSER STREAM FIX] 📄 Processed as raw content:', {
                  requestId,
                  rawContentLength: dataContent.length,
                  totalContentLength: fullContent.length
                });
              }
            }
          }
        }
      }
    } catch (error) {
      // STEP 1: Better error handling - complete gracefully if content received
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[SSE PARSER STREAM FIX] ❌ SSE stream error:', {
          error: error.message,
          requestId,
          eventCount,
          hasReceivedContent,
          contentLength: fullContent.length
        });
        
        // If we received content, complete gracefully
        if (hasReceivedContent && callbacks.onComplete) {
          console.log('[SSE PARSER STREAM FIX] 🔄 Completing gracefully despite error');
          callbacks.onComplete({
            requestId,
            messageId: crypto.randomUUID(),
            content: fullContent,
            suggestions: [],
            model: 'gpt-4',
            usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
            knowledgeUsed: false,
            knowledgeResults: [],
            suggestionsSource: 'api',
            contextTruncated: false,
            queryExpansionUsed: false
          });
        } else {
          throw error;
        }
      }
    } finally {
      clearTimeout(streamTimeoutId);
      try {
        reader.releaseLock();
      } catch (error) {
        // Ignore release errors
      }
    }
  }

  private static parseSSEDataSafely(dataContent: string, requestId: string): any {
    if (!dataContent || dataContent.trim() === '') {
      return null;
    }

    try {
      const parsed = JSON.parse(dataContent);
      
      if (typeof parsed === 'object' && parsed !== null) {
        return parsed;
      }
      
      return null;
      
    } catch (error) {
      console.log('[SSE PARSER STREAM FIX] 🔧 Attempting content extraction from malformed JSON:', {
        requestId,
        error: error instanceof Error ? error.message : String(error)
      });
      
      if (dataContent.includes('"content"') || dataContent.includes('"chunk"')) {
        const contentMatch = dataContent.match(/"content"\s*:\s*"([^"]+)"/);
        if (contentMatch) {
          return {
            type: 'content',
            chunk: contentMatch[1],
            fullContent: contentMatch[1]
          };
        }
      }
      
      return null;
    }
  }

  // STEP 1: Enhanced event handling without blocking
  private static handleSSEEventWithoutBlocking(
    eventData: any,
    callbacks: StreamingCallbacks,
    requestId: string,
    currentFullContent: string
  ): void {
    if (!eventData) return;
    
    try {
      const { type } = eventData;
      const eventRequestId = eventData.requestId || requestId;

      console.log('[SSE PARSER STREAM FIX] 🎯 Processing SSE event (no blocking):', {
        type,
        requestId: eventRequestId,
        eventSequence: eventData.sequence
      });

      switch (type) {
        case 'start':
          // STEP 1: Allow all start events to proceed
          console.log('[SSE PARSER STREAM FIX] 🚀 SSE start event (allowed):', { 
            requestId: eventRequestId,
            model: eventData.model
          });
          if (callbacks.onStart) {
            callbacks.onStart(eventData);
            ConnectionManager.markStartEventFired(requestId);
          }
          break;

        case 'content':
          const { chunk, fullContent } = eventData;
          if (chunk !== undefined && fullContent !== undefined && callbacks.onContent) {
            console.log('[SSE PARSER STREAM FIX] 📄 SSE content event (immediate):', {
              requestId: eventRequestId,
              chunkLength: chunk.length,
              fullContentLength: fullContent.length
            });
            callbacks.onContent(chunk, fullContent, eventRequestId);
          }
          break;

        case 'complete':
          console.log('[SSE PARSER STREAM FIX] 🏁 SSE complete event:', { 
            requestId: eventRequestId,
            contentLength: eventData.content?.length || 0
          });
          ConnectionManager.updateConnectionStatus(requestId, 'completed');
          const completeEvent = { ...eventData, requestId: eventRequestId };
          if (callbacks.onComplete) {
            callbacks.onComplete(completeEvent);
          }
          break;

        case 'error':
          console.error('[SSE PARSER STREAM FIX] ❌ SSE error event:', {
            error: eventData.error,
            requestId: eventRequestId
          });
          if (callbacks.onError) {
            callbacks.onError(eventData.error, eventRequestId);
          }
          break;

        case 'usage':
          if (eventData.usage && callbacks.onUsage) {
            callbacks.onUsage(eventData.usage);
          }
          break;

        default:
          console.log('[SSE PARSER STREAM FIX] ❓ Unknown SSE event type (processing anyway):', {
            requestId: eventRequestId,
            eventType: type
          });
      }
    } catch (error) {
      console.error('[SSE PARSER STREAM FIX] ❌ Error handling SSE event (non-fatal):', {
        error: error instanceof Error ? error.message : String(error),
        requestId,
        eventType: eventData?.type
      });
    }
  }
}
